import { useState, useRef } from "react";
import { todayChallenges } from "../../../mocks/dashboard";

type Challenge = (typeof todayChallenges)[0];

interface Props {
  onComplete: (id: number) => void;
  onSkip: (id: number) => void;
  completedIds: number[];
}

function SwipeCard({
  challenge,
  isTop,
  onComplete,
  onSkip,
}: {
  challenge: Challenge;
  isTop: boolean;
  onComplete: () => void;
  onSkip: () => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const currentX = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState(0);
  const [exiting, setExiting] = useState<"left" | "right" | null>(null);

  const THRESHOLD = 100;

  const onPointerDown = (e: React.PointerEvent) => {
    if (!isTop) return;
    startX.current = e.clientX;
    setDragging(true);
    cardRef.current?.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    currentX.current = e.clientX - startX.current;
    setOffset(currentX.current);
  };

  const onPointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    if (currentX.current > THRESHOLD) {
      setExiting("right");
      setTimeout(onComplete, 350);
    } else if (currentX.current < -THRESHOLD) {
      setExiting("left");
      setTimeout(onSkip, 350);
    } else {
      setOffset(0);
    }
    currentX.current = 0;
  };

  const rotate = offset / 20;
  const opacity = exiting ? 0 : Math.max(0, 1 - Math.abs(offset) / 300);

  const getSwipeHint = () => {
    if (offset > 40) return { label: "완료! ✅", color: "#00D873", side: "right" };
    if (offset < -40) return { label: "건너뛰기 →", color: "#FF6B6B", side: "left" };
    return null;
  };
  const hint = getSwipeHint();

  return (
    <div
      ref={cardRef}
      className="absolute inset-0 bg-white rounded-3xl cursor-grab active:cursor-grabbing select-none"
      style={{
        transform: exiting === "right"
          ? "translateX(120%) rotate(20deg)"
          : exiting === "left"
          ? "translateX(-120%) rotate(-20deg)"
          : `translateX(${offset}px) rotate(${rotate}deg)`,
        opacity,
        transition: dragging ? "none" : "transform 0.35s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.35s",
        zIndex: isTop ? 10 : 5,
        touchAction: "none",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {/* Swipe Hint Overlays */}
      {hint && (
        <div
          className="absolute inset-0 rounded-3xl flex items-center justify-center z-20 pointer-events-none"
          style={{
            background: `${hint.color}15`,
            border: `3px solid ${hint.color}`,
          }}
        >
          <span className="text-2xl font-black" style={{ color: hint.color }}>
            {hint.label}
          </span>
        </div>
      )}

      <div className="p-7 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <span
            className="text-xs font-bold px-3 py-1.5 rounded-full"
            style={{
              backgroundColor: `${challenge.categoryColor}20`,
              color: challenge.categoryColor,
            }}
          >
            {challenge.category}
          </span>
          <div className="flex items-center gap-1.5 text-[#191F28]/40 text-xs">
            <i className="ri-time-line" />
            <span>{challenge.duration}</span>
          </div>
        </div>

        {/* Icon */}
        <div
          className="w-14 h-14 flex items-center justify-center rounded-2xl mb-5"
          style={{ backgroundColor: `${challenge.categoryColor}15` }}
        >
          <i className={`${challenge.icon} text-3xl`} style={{ color: challenge.categoryColor }} />
        </div>

        {/* Content */}
        <div className="flex-1">
          <h3 className="text-xl font-black text-[#191F28] mb-3 leading-tight">{challenge.title}</h3>
          <p className="text-sm text-[#191F28]/60 leading-relaxed">{challenge.desc}</p>
        </div>

        {/* Points */}
        <div className="flex items-center justify-between pt-5 border-t border-[#F2F4F6]">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-[#FFD600] flex items-center justify-center">
              <span className="text-[9px] font-black text-white">₩</span>
            </div>
            <span className="font-black text-[#191F28]">+{challenge.points}원</span>
          </div>
          <span className="text-xs text-[#191F28]/30 font-medium">← 건너뛰기 | 완료 →</span>
        </div>
      </div>
    </div>
  );
}

export default function SwipeChallengeSection({
  onComplete,
  onSkip,
  completedIds,
}: Props) {
  const remaining = todayChallenges.filter((c) => !completedIds.includes(c.id));
  const [cardQueue, setCardQueue] = useState(remaining.map((c) => c.id));

  const handleComplete = (id: number) => {
    onComplete(id);
    setCardQueue((q) => q.filter((x) => x !== id));
  };
  const handleSkip = (id: number) => {
    onSkip(id);
    setCardQueue((q) => q.filter((x) => x !== id));
  };

  const visibleCards = cardQueue
    .slice(0, 2)
    .map((id) => todayChallenges.find((c) => c.id === id)!)
    .filter(Boolean);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-2xl font-black text-[#191F28]">오늘 해낼 1도 부담 없는 업무 지시</h2>
          <p className="text-sm text-[#191F28]/40 mt-0.5">오른쪽 → 완료 &nbsp;|&nbsp; 왼쪽 ← 건너뛰기</p>
        </div>
        <div className="bg-[#F2F4F6] px-4 py-2 rounded-full text-sm font-bold text-[#191F28]">
          {cardQueue.length}/{todayChallenges.length}
        </div>
      </div>

      {/* Card Stack */}
      <div className="relative" style={{ height: 340 }}>
        {visibleCards.length === 0 ? (
          <div className="absolute inset-0 bg-white rounded-3xl flex flex-col items-center justify-center gap-4">
            <div className="text-5xl">🎉</div>
            <div className="text-xl font-black text-[#191F28]">오늘의 파이프라인 공사 완료!</div>
            <div className="text-sm text-[#191F28]/50">푹 쉬세오. 내일 또 같이 벌어요 🤑</div>
          </div>
        ) : (
          <>
            {/* Background card hint */}
            {visibleCards.length > 1 && (
              <div
                className="absolute inset-0 bg-[#F2F4F6] rounded-3xl"
                style={{ transform: "scale(0.95) translateY(8px)", zIndex: 1 }}
              />
            )}
            {/* Top card */}
            <SwipeCard
              key={visibleCards[0].id}
              challenge={visibleCards[0]}
              isTop
              onComplete={() => handleComplete(visibleCards[0].id)}
              onSkip={() => handleSkip(visibleCards[0].id)}
            />
          </>
        )}
      </div>

      {/* Buttons */}
      {visibleCards.length > 0 && (
        <div className="flex gap-3 mt-5">
          <button
            onClick={() => handleSkip(visibleCards[0].id)}
            className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl bg-[#F2F4F6] text-[#191F28]/60 font-bold text-sm hover:bg-red-50 hover:text-red-400 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" /> 건너뛰기
          </button>
          <button
            onClick={() => handleComplete(visibleCards[0].id)}
            className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl bg-[#00D873] text-white font-bold text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
          >
            완료하기 <i className="ri-arrow-right-line" />
          </button>
        </div>
      )}
    </div>
  );
}
